import type { LoginAvatar, LoginAvatarTransform, LoginBreakpoint } from "@htzap/contracts";
import { API_URL } from "../../lib/api";

const builtinAssets = import.meta.glob<string>("../../assets/login-team/avatar-*.png", { eager: true, query: "?url", import: "default" });
const builtinByName = Object.fromEntries(Object.entries(builtinAssets).map(([path, src]) => [path.split(/[\\/]/).pop()!.toLowerCase(), src]));

export function avatarImageUrl(avatar: Pick<LoginAvatar, "assetKind" | "assetKey" | "imageUrl">) {
  return avatar.assetKind === "BUILTIN" ? builtinByName[avatar.assetKey.toLowerCase()] || "" : `${API_URL}${avatar.imageUrl}`;
}

export function transformFor(avatar: LoginAvatar, breakpoint: LoginBreakpoint): LoginAvatarTransform {
  return breakpoint === "mobile" ? avatar.mobile || avatar.tablet || avatar.base : breakpoint === "tablet" ? avatar.tablet || avatar.base : avatar.base;
}

export function clampTransform(transform: LoginAvatarTransform): LoginAvatarTransform {
  return { x: Math.min(1, Math.max(0, transform.x)), y: Math.min(1, Math.max(0.18, transform.y)), scale: Math.min(1.4, Math.max(0.2, transform.scale)) };
}

export function normalizeLayers<T extends { id: string; zIndex: number }>(avatars: T[]): T[] {
  return [...avatars].sort((a, b) => a.zIndex - b.zIndex || a.id.localeCompare(b.id)).map((avatar, zIndex) => ({ ...avatar, zIndex }));
}

export function adjustFeet<T extends LoginAvatar>(avatars: T[], breakpoint: LoginBreakpoint): T[] {
  const active = avatars.filter((avatar) => avatar.isActive).sort((a, b) => transformFor(a, breakpoint).x - transformFor(b, breakpoint).x);
  const changes = new Map<string, LoginAvatarTransform>();
  for (let index = 1; index < active.length; index += 1) {
    const previous = changes.get(active[index - 1]!.id) || transformFor(active[index - 1]!, breakpoint);
    const current = changes.get(active[index]!.id) || transformFor(active[index]!, breakpoint);
    const previousHalf = previous.scale * (active[index - 1]!.originalWidth / active[index - 1]!.originalHeight) * 0.13;
    const currentHalf = current.scale * (active[index]!.originalWidth / active[index]!.originalHeight) * 0.13;
    const minimumX = previous.x + previousHalf + currentHalf + 0.008;
    if (current.x < minimumX) changes.set(active[index]!.id, { ...current, x: Math.min(0.97, minimumX) });
  }
  if (!changes.size) return avatars;
  return avatars.map((avatar) => {
    const transform = changes.get(avatar.id);
    if (!transform) return avatar;
    return breakpoint === "base" ? { ...avatar, base: transform } : breakpoint === "tablet" ? { ...avatar, tablet: transform } : { ...avatar, mobile: transform };
  });
}

export function compositionWarnings(avatars: LoginAvatar[], breakpoint: LoginBreakpoint) {
  const warnings: string[] = [];
  const active = avatars.filter((avatar) => avatar.isActive);
  for (const avatar of active) {
    const item = transformFor(avatar, breakpoint);
    if (item.x < 0.025 || item.x > 0.975 || item.y > 0.985) warnings.push(`${avatar.label} ultrapassa a área segura.`);
    if (item.x > 0.58 && breakpoint !== "mobile") warnings.push(`${avatar.label} pode sobrepor o formulário.`);
  }
  return [...new Set(warnings)].slice(0, 4);
}
